import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { track } from "@/lib/analytics";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Business challenge cards — short recognisable commercial problems,
 * each optionally linking to a relevant solution page.
 * challenges: [{ title, body?, to? }]
 */
export default function ChallengeCards({
  testid,
  badge,
  headline,
  intro,
  challenges,
  extra,
  kicker,
  tone = "light",
}) {
  const dark = tone === "dark";
  return (
    <section
      data-testid={testid}
      className={`py-16 md:py-20 ${dark ? "bg-[#050505] text-white" : "bg-white"}`}
    >
      <div className={GRID}>
        <SectionBadge label={badge} tone={dark ? "light" : undefined} />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-2xl">
              {headline}
            </h2>
          </div>
          {intro && (
            <div className="col-span-12 md:col-span-6 md:flex md:flex-col md:justify-end">
              <p className={`text-base leading-relaxed max-w-lg ${dark ? "text-white/70" : "text-zinc-600"}`}>
                {intro}
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 reveal stagger-reveal">
          {challenges.map((c, i) => {
            const inner = (
              <>
                {c.image && (
                  <div className="-mx-7 -mt-7 mb-5 aspect-[16/10] overflow-hidden bg-zinc-200">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-serif-display text-4xl leading-none text-black/15 transition-colors duration-500 group-hover:text-black/45">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-zinc-300 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <h3 className="font-serif-display text-xl md:text-2xl leading-tight text-black mt-5">
                  {c.title}
                </h3>
                {c.body && (
                  <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed flex-1">
                    {c.body}
                  </p>
                )}
                {c.to && (
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-black">
                    Explore the solution
                    <ArrowUpRight className="h-4 w-4 text-zinc-400 transition-colors group-hover:text-black" />
                  </span>
                )}
                <span
                  aria-hidden
                  className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
                />
              </>
            );
            const cls =
              "group relative flex flex-col bg-white border border-zinc-200 p-7 transition-all duration-500 hover:border-black hover:-translate-y-1";
            if (c.to?.startsWith("#")) {
              return (
                <a
                  key={c.title}
                  href={c.to}
                  data-testid={`${testid}-card-${i}`}
                  onClick={() => track("challenge_card_open", { challenge: c.title })}
                  className={cls}
                >
                  {inner}
                </a>
              );
            }
            return c.to ? (
              <Link
                key={c.title}
                to={c.to}
                data-testid={`${testid}-card-${i}`}
                onClick={() => track("challenge_card_open", { challenge: c.title })}
                className={cls}
              >
                {inner}
              </Link>
            ) : (
              <div key={c.title} data-testid={`${testid}-card-${i}`} className={cls}>
                {inner}
              </div>
            );
          })}
        </div>

        {extra?.length > 0 && (
          <div className="mt-8" data-testid={`${testid}-extra`}>
            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-zinc-500">
              Also worth diagnosing
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {extra.map((x) => (
                <span
                  key={x}
                  className="inline-flex items-center h-7 px-3 border border-black/15 text-xs text-zinc-700 bg-white"
                >
                  {x}
                </span>
              ))}
            </div>
          </div>
        )}

        {kicker && (
          <p
            data-testid={`${testid}-kicker`}
            className={`mt-10 font-serif-display text-xl md:text-2xl italic leading-snug ${dark ? "text-white/90" : "text-black"}`}
          >
            {kicker}
          </p>
        )}
      </div>
    </section>
  );
}
